import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core'
import { ButtonComponent } from '@core/components/button/button.component'
import { IconComponent } from '@core/components/icon/icon.component'
import { InputComponent } from '@core/components/input/input.component'
import { ClickOutsideDirective } from '@core/directives/click-outside.directive'

type IDatepickerMode = 'calendar' | 'months' | 'years'

@Component({
	selector: 'epic-datepicker',
	standalone: true,
	imports: [CommonModule, ButtonComponent, ClickOutsideDirective, IconComponent],
	templateUrl: './datepicker.component.html',
	styleUrl: './datepicker.component.scss',
})
export class DatepickerComponent implements AfterViewInit {
	numberOfDaysInAMonth!: number
	offset = 0
	year!: number
	daysOfTheWeek: string[] = [
		$localize`Mon`, // Monday
		$localize`Tue`, // Tuesday
		$localize`Wed`, // Wednesday
		$localize`Thu`, // Thursday
		$localize`Fri`, // Friday
		$localize`Sat`, // Saturday
		$localize`Sun`, // Sunday
	]
	years!: number[]
	months: string[] = [
		$localize`January`, // styczeń
		$localize`February`, // luty
		$localize`March`, // marzec
		$localize`April`, // kwiecień
		$localize`May`, // maj
		$localize`June`, // czerwiec
		$localize`July`, // lipiec
		$localize`August`, // sierpień
		$localize`September`, // wrzesień
		$localize`October`, // październik
		$localize`November`, // listopad
		$localize`December`, // grudzień
	]

	mode: IDatepickerMode = 'calendar'
	middleYear!: number
	inputXRef?: InputComponent
	inputYRef?: InputComponent
	isVisible = false
	min?: Date
	max?: Date
	selected?: string
	isDateRange?: boolean

	protected readonly Array = Array
	private monthIndex!: number
	private now!: Date
	private _firstClickOutside = false

	@ViewChild('container') container?: ElementRef

	constructor(public elementRef: ElementRef<HTMLElement>) {
		this.init()
	}

	/**
	 * Retrieves the current month.
	 *
	 * @returns {string} The name of the current month.
	 */
	get month(): string {
		return this.months[this.monthIndex]
	}

	get top(): string {
		return this.expandFromTop ? this.getTopPosition() + 'px' : 'unset'
	}

	get bottom(): string {
		return this.expandFromTop ? 'unset' : this.getBottomPosition() + 'px'
	}

	get left(): string {
		return this.getLeftPosition() + 'px'
	}

	get expandFromTop() {
		if (!this.inputXRef) {
			return true
		}

		const calendarHeight = this.elementRef.nativeElement.querySelector('.epic-datepicker')?.clientHeight ?? 0
		const inputRect = this.inputXRef.elementRef.nativeElement.getBoundingClientRect()
		const wHeight = window.innerHeight

		return calendarHeight + inputRect.bottom + 10 < wHeight || inputRect.top - calendarHeight - 10 < 0
	}

	/**
	 * Check if the given day is less than the minimum allowed day.
	 *
	 * @param {number} day - The day value.
	 * @return {boolean} - True if the day is less than the minimum allowed day, false otherwise.
	 */
	isDayLessThanMin(day: number): boolean | undefined {
		return this.min && new Date(this.year, this.monthIndex, day) < this.min
	}

	/**
	 * Checks if the given day is greater than the maximum date.
	 *
	 * @param {number} day - The day to check.
	 * @returns {boolean|undefined} - Returns `true` if the day is greater than the maximum date, `false` otherwise. Returns `undefined` if the maximum date is not provided.
	 */
	isDayGreaterThanMax(day: number): boolean | undefined {
		return this.max && new Date(this.year, this.monthIndex, day) > this.max
	}

	ngAfterViewInit() {}

	/**
	 * Increment the current selection to the next period.
	 */
	next() {
		if (this.mode === 'years') {
			this.nextYears()
		} else {
			this.nextMonth()
		}
	}

	/**
	 * Increment the current selection to the previous period.
	 */
	previous() {
		if (this.mode === 'years') {
			this.previousYears()
		} else {
			this.previousMonth()
		}
	}

	/**
	 * Checks if the provided day is the current date.
	 *
	 * @param {number} day - The day to check.
	 * @returns {boolean} - Returns true if the provided day is the current date, otherwise false.
	 */
	isCurrentDate(day: number): boolean {
		return this.year === this.now.getFullYear() && this.monthIndex === this.now.getMonth() && day === this.now.getDate()
	}

	isSelectedDate(day: number): boolean {
		const date = new Date(this.year, this.monthIndex, day)

		if (this.isDateRange) {
			const startDate = this.inputXRef?.control?.value ? new Date(this.inputXRef.control.value) : null
			const endDate = this.inputYRef?.control?.value ? new Date(this.inputYRef.control.value) : null

			if (startDate && date.getTime() === startDate.getTime()) {
				return true
			}

			if (endDate && date.getTime() === endDate.getTime()) {
				return true
			}

			if (startDate && endDate) {
				return date > startDate && date < endDate
			}
		}
		return <boolean>(this.selected && this.selected === `${this.year}-${this.monthIndex + 1}-${day}`)
	}

	/**
	 * Selects a date from the calendar.
	 *
	 * @param {number} day - The day of the month to select.
	 *
	 * @return {void}
	 */
	selectDate(day: number): void {
		const result = new Date(this.year, this.monthIndex, day)

		if (this.isDateRange) {
			setTimeout(() => {
				if (!this.inputXRef?.control?.value && !this.inputYRef?.control?.value) {
					this.inputXRef?.control?.patchValue(result, { emitEvent: false })
				} else if (this.inputXRef?.control?.value && !this.inputYRef?.control?.value) {
					const startDate = this.inputXRef.control.value
					if (result < startDate) {
						this.inputYRef?.control?.patchValue(startDate, { emitEvent: false })
						this.inputXRef?.control?.patchValue(result, { emitEvent: false })
					} else {
						this.inputYRef?.control?.patchValue(result, { emitEvent: false })
					}
					this.close()
				} else {
					this.inputXRef?.control?.reset()
					this.inputYRef?.control?.reset()
					this.inputXRef?.control?.patchValue(result, { emitEvent: false })
				}

				if (!!this.inputXRef?.control?.value || !!this.inputYRef?.control?.value) {
					if (this.inputYRef) {
						this.inputYRef.isActive = true
					}
					if (this.inputXRef) {
						this.inputXRef.isActive = true
					}
				}
			})

			return
		}

		if (this.inputXRef) {
			this.inputXRef.control?.patchValue(result, {
				emitEvent: true,
			})
			this.inputXRef.model = result
			this.inputXRef.modelChange.emit(result)
			this.close()
		}
	}

	/**
	 * Sets the year and mode for the selection.
	 *
	 * @param {number} year - The year to select.
	 * @return {void}
	 */
	selectYear(year: number): void {
		this.year = year
		this.setMode('months')
	}

	/**
	 * Sets the month index.
	 *
	 * @param {number} monthIndex - The new month index to set.
	 * @return {void}
	 */
	selectMonth(monthIndex: number): void {
		if (monthIndex >= 0 && monthIndex <= 11) {
			this.setMonthIndex(monthIndex)
		}
		this.setMode('calendar')
	}

	/**
	 * Handles the click event outside of the component.
	 * @returns {void}
	 */
	afterClickOutside(): void {
		if (this.isDateRange && (!this.inputXRef?.control?.value || !this.inputYRef?.control?.value)) {
			this._firstClickOutside = true
			this.isVisible = false
			return
		}
		if (this._firstClickOutside) {
			this.close()
		} else {
			this._firstClickOutside = true
		}
	}

	/**
	 * Sets the mode of the datepicker.
	 *
	 * @param {IDatepickerMode} mode - The mode to set. Valid values are 'years' and any other mode supported by the datepicker.
	 *
	 * @return {void}
	 */
	setMode(mode: IDatepickerMode): void {
		if (mode === 'years') {
			this.middleYear = this.year
		}

		this.mode = mode
	}

	/**
	 * Opens the calendar and initializes its mode.
	 *
	 * @param {Date} [date] - The optional date to be set on opening the calendar.
	 *
	 * @returns {void}
	 */
	open(): void {
		setTimeout(() => {
			this.mode = 'calendar'
			this.init()
		})
		this.isVisible = true
		if (this.inputXRef) {
			this.inputXRef.isActive = true
		}
		if (this.isDateRange && this.inputYRef) {
			this.inputYRef.isActive = true
		}
		setTimeout(() => {
			this._firstClickOutside = true
		}, 250)
	}

	/**
	 * Closes the method.
	 *
	 * @returns {void}
	 */
	close(): void {
		if (this.isDateRange && (!this.inputXRef?.control?.value || !this.inputYRef?.control?.value)) {
			return
		}

		if (this.inputXRef) {
			this.inputXRef.isActive = false
		}

		this.isVisible = false
		this.inputXRef = undefined
		this.inputYRef = undefined
		this._firstClickOutside = false
		this.min = undefined
		this.max = undefined
	}

	private getBottomPosition(): number {
		if (this.inputXRef?.elementRef.nativeElement) {
			return window.innerHeight - this.inputXRef.elementRef.nativeElement.getBoundingClientRect().top + 10
		} else {
			return 0
		}
	}

	/**
	 * Retrieves the top position of the element.
	 *
	 * @returns {number} The top position of the element.
	 */
	private getTopPosition(): number {
		if (this.inputXRef?.elementRef.nativeElement) {
			return (
				this.inputXRef.elementRef.nativeElement.getBoundingClientRect().top +
				this.inputXRef.elementRef.nativeElement.clientHeight +
				10
			)
		} else {
			return 0
		}
	}

	/**
	 * Retrieves the left position of the native element associated with inputXRef.
	 * @returns {number} The left position of the native element or 0 if it is not available.
	 */
	private getLeftPosition(): number {
		if (this.inputXRef?.elementRef?.nativeElement) {
			const elementLeft = this.inputXRef.elementRef.nativeElement.getBoundingClientRect().left
			const calendarWidth = this.container?.nativeElement.offsetWidth ?? 0
			const screenWidth = window.innerWidth

			return Math.min(elementLeft, screenWidth - calendarWidth - 20)
		} else {
			return 0
		}
	}

	/**
	 * Initializes the variables for the current year, month, and number of days in a month.
	 *
	 * @private
	 * @function init
	 * @returns {void}
	 */
	private init(): void {
		this.now = new Date()
		this.year = this.now.getFullYear()
		this.monthIndex = this.now.getMonth()
		this.setNumberOfDaysInAMonth()
	}

	/**
	 * Increases the current month index by 1.
	 * If the current month index is 12, increment the year by 1 and reset the month index to 0.
	 * Updates the numberOfDaysInAMonth property based on the updated year and month index.
	 *
	 * @return {void}
	 */
	private nextMonth(): void {
		if (this.monthIndex < 11) {
			this.monthIndex++
		} else {
			this.year++
			this.monthIndex = 0
		}

		this.setNumberOfDaysInAMonth()
	}

	/**
	 * Decreases the current month by one. If the current month is January, it reduces the year by one and sets the current month to December.
	 * Also updates the number of days in the month according to the updated month and year.
	 *
	 * @returns {void} No return value.
	 */
	private previousMonth(): void {
		if (this.monthIndex > 0) {
			this.monthIndex--
		} else {
			this.year--
			this.monthIndex = 11
		}

		this.setNumberOfDaysInAMonth()
	}

	/**
	 * Increment the `middleYear` property value by 15.
	 *
	 * @private
	 * @returns {void}
	 */
	private nextYears(): void {
		this.middleYear += 15
	}

	/**
	 * Decreases the middle year value by 15, representing the previous years.
	 *
	 * @private
	 * @returns {void}
	 */
	private previousYears(): void {
		this.middleYear -= 15
	}

	/**
	 * Calculates the number of days in a given month.
	 *
	 * @param {number} year - The year.
	 * @param {number} monthIndex - The index of the month (0-11).
	 * @return {number} - The number of days in the month.
	 */
	private getNumberOfDaysInAMonth(year: number, monthIndex: number): number {
		return new Date(year, monthIndex + 1, 0).getDate()
	}

	/**
	 * Sets the offset value for the current date instance.
	 * The offset represents the number of days to shift the first day of the month.
	 *
	 * @private
	 */
	private setOffset() {
		this.offset = (new Date(this.year, this.monthIndex, 1).getDay() + 6) % 7
	}

	/**
	 * Sets the number of days in a month for the current year and month.
	 * Updates the `numberOfDaysInAMonth` property accordingly.
	 * Also updates the offset based on the new number of days.
	 *
	 * @private
	 * @returns {void}
	 */
	private setNumberOfDaysInAMonth(): void {
		this.numberOfDaysInAMonth = this.getNumberOfDaysInAMonth(this.year, this.monthIndex)
		this.setOffset()
	}

	/**
	 * Sets the month index and updates the number of days in a month.
	 *
	 * @param {number} index - The new month index.
	 * @return {void}
	 */
	private setMonthIndex(index: number): void {
		this.monthIndex = index
		this.setNumberOfDaysInAMonth()
	}
}
